import { useI18n } from 'vue-i18n';

// короткие метки для единиц времени (для компактного отображения)
const SHORT_LABELS = {
  en: { s: 's', m: 'm', h: 'h', d: 'd' },
  ru: { s: 'с', m: 'м', h: 'ч', d: 'д' },
  pl: { s: 's', m: 'min', h: 'h', d: 'd' },
  es: { s: 's', m: 'min', h: 'h', d: 'd' },
  kk: { s: 'с', m: 'мин', h: 'сағ', d: 'күн' },
  bg: { s: 'с', m: 'мин', h: 'ч', d: 'д' },
};

// словари единиц времени
const timeUnits = {
  en: {
    sec: ['second', 'seconds', 'seconds'],
    min: ['minute', 'minutes', 'minutes'],
    hr: ['hour', 'hours', 'hours'],
    day: ['day', 'days', 'days'],
  },
  ru: {
    sec: ['секунда', 'секунды', 'секунд'],
    min: ['минута', 'минуты', 'минут'],
    hr: ['час', 'часа', 'часов'],
    day: ['день', 'дня', 'дней'],
  },
  pl: {
    sec: ['sekunda', 'sekundy', 'sekund'],
    min: ['minuta', 'minuty', 'minut'],
    hr: ['godzina', 'godziny', 'godzin'],
    day: ['dzień', 'dni', 'dni'],
  },
  es: {
    sec: ['segundo', 'segundos', 'segundos'],
    min: ['minuto', 'minutos', 'minutos'],
    hr: ['hora', 'horas', 'horas'],
    day: ['día', 'días', 'días'],
  },
  kk: {
    sec: ['секунд', 'секунд', 'секунд'],
    min: ['минут', 'минут', 'минут'],
    hr: ['сағат', 'сағат', 'сағат'],
    day: ['күн', 'күн', 'күн'],
  },
  bg: {
    sec: ['секунда', 'секунди', 'секунди'],
    min: ['минута', 'минути', 'минути'],
    hr: ['час', 'часа', 'часа'],
    day: ['ден', 'дни', 'дни'],
  },
};

// вспомогательная функция для правильных окончаний
function pluralize(num, [one, few, many], locale) {
  if (['en', 'es', 'kk', 'bg'].includes(locale)) {
    return num === 1 ? one : many;
  }
  if (['ru', 'pl'].includes(locale)) {
    if (num % 10 === 1 && num % 100 !== 11) return one;
    if (num % 10 >= 2 && num % 10 <= 4 && (num % 100 < 10 || num % 100 >= 20))
      return few;
    return many;
  }
  return many;
}

// основная функция форматирования
function formatTimeLocalized(timeInSeconds, locale = 'en') {
  const t = timeUnits[locale] || timeUnits.en;

  if (timeInSeconds >= 86400) {
    const days = Math.floor(timeInSeconds / 86400);
    const hours = Math.floor((timeInSeconds % 86400) / 3600);
    const dayLabel = pluralize(days, t.day, locale);
    const hourLabel = pluralize(hours, t.hr, locale);
    return `${days} ${dayLabel}${hours > 0 ? ` ${hours} ${hourLabel}` : ''}`;
  }

  if (timeInSeconds >= 3600) {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const hourLabel = pluralize(hours, t.hr, locale);
    const minLabel = pluralize(minutes, t.min, locale);
    return `${hours} ${hourLabel}${minutes > 0 ? ` ${minutes} ${minLabel}` : ''}`;
  }

  if (timeInSeconds >= 60) {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    const minLabel = pluralize(minutes, t.min, locale);
    const secLabel = pluralize(seconds, t.sec, locale);
    return `${minutes} ${minLabel}${seconds > 0 ? ` ${seconds} ${secLabel}` : ''}`;
  }

  const secLabel = pluralize(timeInSeconds, t.sec, locale);
  return `${Math.floor(timeInSeconds)} ${secLabel}`;
}

// форматирование длительности в минутах в коротком формате (например, "8ч 30м")
function formatDurationLocalized(totalMinutes, locale = 'en') {
  if (!totalMinutes && totalMinutes !== 0) return '';

  const l = SHORT_LABELS[locale] || SHORT_LABELS.en;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (hours > 0) {
    parts.push(`${hours}${l.h}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}${l.m}`);
  }

  return parts.length > 0 ? parts.join(' ') : `0${l.m}`;
}

export function useTimeFormatter() {
  const { locale } = useI18n();

  const formatTime = seconds => formatTimeLocalized(seconds, locale.value);

  const formatDuration = minutes => {
    const localeCode = locale.value?.split('-')[0] || 'en';
    return formatDurationLocalized(minutes, localeCode);
  };

  return { formatTime, formatDuration };
}
